import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, AlertTriangle, MapPin, DollarSign, Train, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import { trackEvent } from '../lib/api';
import SEO from '../components/SEO';
import { GA_EVENTS } from '../lib/analytics';

export default function BuyerBlueprintLanding() {
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    trackEvent({ event_type: 'page_view', page: 'burlington-buyer-blueprint' });
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleCTA = () => { GA_EVENTS.ctaClicked('blueprint-landing'); };

  return (
    <div className="min-h-screen" data-testid="blueprint-landing">
      <SEO
        title="Find the Right Burlington Neighbourhood — Before You Overpay"
        description="Get a personalized breakdown of where you should live in Burlington based on your budget and lifestyle. See what homes actually sell for. Takes 30 seconds."
        canonical="/burlington-buyer-blueprint"
      />

      {/* ─── HERO ─── */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20">
        <div className="max-w-3xl mx-auto px-6 text-center md:text-left">
          <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-normal leading-[1.1] tracking-tight text-slate-900 mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Find the right Burlington neighbourhood — <span className="italic">before you overpay</span>
          </h1>
          <div className="text-lg text-slate-500 leading-relaxed mb-8 space-y-1">
            <p>Get a personalized breakdown of:</p>
            <div className="space-y-2 mt-3 text-left max-w-md mx-auto md:mx-0">
              {[
                'Where you should live (based on your budget + lifestyle)',
                'What homes actually sell for (not just list for)',
                'Which areas are worth it — and which to avoid',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-base text-slate-600">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <Link to="/blueprint" onClick={handleCTA} data-testid="hero-cta">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-4 h-auto text-base font-semibold shadow-xl shadow-slate-900/20 hover:-translate-y-0.5 transition-all">
              See my Burlington plan <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="text-xs text-slate-400 mt-4">Takes 30 seconds. Built for serious buyers looking in Burlington, Ontario.</p>
        </div>
      </section>

      {/* ─── VALUE BAR ─── */}
      <section className="bg-slate-50 border-y border-slate-100 py-4">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-500 font-medium">No spam. No pressure. Just clarity before you buy.</p>
        </div>
      </section>

      {/* ─── WHY MOST BUYERS GET THIS WRONG ─── */}
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl tracking-tight mb-6 text-center md:text-left" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Why most buyers <span className="italic">get this wrong</span>
          </h2>
          <div className="space-y-4 text-base text-slate-600 leading-relaxed">
            <p>Most people start in the wrong place.</p>
            <p className="text-slate-900 font-medium">Most buyers start with listings. That's the mistake.</p>
            <p>Listings don't tell you:</p>
            <div className="space-y-2 my-4">
              {[
                'What areas actually fit your lifestyle',
                'Where your budget goes the furthest',
                'Which neighbourhoods are quietly overpriced',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-slate-50 rounded-xl p-3">
                  <span className="text-red-400 font-bold mt-0.5">x</span>
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
            <p>So people end up: <span className="font-medium text-slate-900">overpaying, choosing the wrong area, or missing better opportunities entirely.</span></p>
          </div>
        </div>
      </section>

      {/* ─── RESULTS PREVIEW ─── */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl tracking-tight mb-8 text-center" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Here's what your Burlington plan <span className="italic">looks like</span>
          </h2>

          {/* Example Profile */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Example Buyer Profile</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-2.5">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                <div>
                  <p className="text-xs text-slate-400">Budget</p>
                  <p className="text-sm font-semibold text-slate-900">$900,000</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <MapPin className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs text-slate-400">Home Type</p>
                  <p className="text-sm font-semibold text-slate-900">2-3 bedroom</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <Train className="w-4 h-4 text-amber-500" />
                <div>
                  <p className="text-xs text-slate-400">Commute</p>
                  <p className="text-sm font-semibold text-slate-900">Toronto 2-3x/wk</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recommended Areas */}
          <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-emerald-500" /> Recommended areas for this profile:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {[
              {
                name: 'Tyandaga',
                tag: 'Best Value',
                tagColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                points: ['More space for your budget', 'Quiet, family-friendly streets', 'Strong long-term value'],
              },
              {
                name: 'Aldershot',
                tag: 'Commuter Pick',
                tagColor: 'bg-blue-50 text-blue-700 border-blue-200',
                points: ['Easier commute (GO + highway access)', 'Mix of older + newer homes', 'Often overlooked — better value'],
              },
            ].map((area, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 hover-lift">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: "'Instrument Serif', serif" }}>{area.name}</h3>
                  <span className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full border ${area.tagColor}`}>{area.tag}</span>
                </div>
                <div className="space-y-2">
                  {area.points.map((p, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-[13px] text-slate-600">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Caution Area */}
          <div className="bg-amber-50/60 border border-amber-200/60 rounded-2xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <p className="text-sm font-semibold text-amber-900">Areas to be careful with:</p>
            </div>
            <p className="text-sm font-medium text-amber-800 mb-2">Central Burlington (select pockets)</p>
            <div className="space-y-1.5">
              {[
                'Higher prices for similar homes',
                'Competitive, often overbid',
                'Less value compared to nearby options',
              ].map((p, i) => (
                <div key={i} className="flex items-start gap-2">
                  <AlertTriangle className="w-3 h-3 text-amber-500 mt-1 flex-shrink-0" />
                  <span className="text-[13px] text-amber-700">{p}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 italic">
            This is just a sample. Your results are based on your exact budget, lifestyle, and goals.
          </p>
        </div>
      </section>

      {/* ─── PRIMARY CTA ─── */}
      <section className="py-16 md:py-24">
        <div className="max-w-xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Get your personalized <span className="italic">Burlington plan</span>
          </h2>
          <p className="text-base text-slate-500 mb-8">
            Answer a few quick questions and we'll map out where you should be looking — before you start browsing listings.
          </p>
          <Link to="/blueprint" onClick={handleCTA} data-testid="primary-cta">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-10 py-4 h-auto text-lg font-semibold shadow-xl shadow-slate-900/20 hover:-translate-y-0.5 transition-all">
              Start my Buyer Blueprint <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="text-xs text-slate-400 mt-4">Takes ~30 seconds. No commitment required.</p>
        </div>
      </section>

      {/* ─── TRUST / CREDIBILITY ─── */}
      <section className="py-12 bg-slate-50 border-y border-slate-100">
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
            {[
              'Used by buyers making $800K–$1M+ decisions',
              'Focused specifically on Burlington, Ontario',
              'Built using real market insights — not guesswork',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span className="text-sm text-slate-600">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-16 md:py-24">
        <div className="max-w-xl mx-auto px-6 text-center">
          <h3 className="text-2xl md:text-3xl tracking-tight mb-6 text-slate-900" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Make your next move with clarity — <span className="italic">not guesswork</span>
          </h3>
          <Link to="/blueprint" onClick={handleCTA} data-testid="final-cta">
            <Button variant="outline" className="rounded-full px-8 py-3 h-auto text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">
              Find where I should live <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ─── MOBILE STICKY CTA ─── */}
      {showSticky && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 p-3 shadow-lg">
          <Link to="/blueprint" onClick={handleCTA} className="block">
            <Button className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full py-3.5 h-auto text-sm font-semibold">
              Start my Buyer Blueprint <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
